import {BreakpointObserver} from '@angular/cdk/layout';
import {DOCUMENT, effect, inject, Renderer2, RendererFactory2, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {patchState, signalStore, withHooks, withMethods, withState} from '@ngrx/signals';
import {withStorageSync} from '@oleksandroleniuk/ngrx-toolkit';
import {map} from 'rxjs';

export enum ThemeEnum {
  Light = 'light',
  Dark = 'dark',
}

interface ThemeState {
  theme: ThemeEnum | null;
}

const initialState: ThemeState = {
  theme: null,
};

/**
 * Store for managing application theme (light/dark mode)
 *
 * @example
 * ```typescript
 * import {Component, inject} from '@angular/core';
 * import {ThemeStore} from '@scalified-monorepo/ng-shared';
 *
 * @Component({
 *   selector: 'app-theme-switcher',
 *   standalone: true,
 * })
 * export class ThemeSwitcherComponent {
 *   private readonly themeStore = inject(ThemeStore);
 *
 *   toggleTheme() {
 *     this.themeStore.toggleTheme(); // Toggles between light and dark mode
 *   }
 * }
 * ```
 */
export const ThemeStore = signalStore(
  {providedIn: 'root'},
  withState(initialState),
  withStorageSync({
    key: `color-theme`,
    select: ({theme}) => ({theme}),
    stringify: state => (state.theme === null ? 'null' : state.theme),
    parse: value => (value === 'null' ? {theme: null} : {theme: value as ThemeEnum}),
  }),
  withMethods(state => ({
    toggleTheme() {
      patchState(state, {theme: state.theme() === ThemeEnum.Light ? ThemeEnum.Dark : ThemeEnum.Light});
    },
    setTheme(theme: ThemeEnum) {
      patchState(state, {theme});
    },
  })),
  withHooks(
    (
      state,
      rendererFactory = inject(RendererFactory2),
      breakpointObserver = inject(BreakpointObserver),
      document = inject(DOCUMENT)
    ) => ({
      onInit() {
        const renderer: Renderer2 = rendererFactory.createRenderer(null, null);

        const systemDarkMode: Signal<boolean> = toSignal(
          breakpointObserver.observe('(prefers-color-scheme: dark)').pipe(map(result => result.matches)),
          {initialValue: false}
        );

        effect(() => {
          if (state.theme()) {
            return;
          }
          if (systemDarkMode()) {
            state.setTheme(ThemeEnum.Dark);
          } else {
            state.setTheme(ThemeEnum.Light);
          }
        });

        effect(() => {
          if (state.theme() === ThemeEnum.Dark) {
            renderer.addClass(document.documentElement, ThemeEnum.Dark);
          } else {
            renderer.removeClass(document.documentElement, ThemeEnum.Dark);
          }
        });
      },
    })
  )
);
